import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { StarIcon, PlayCircleIcon, SendIcon, CheckCircleIcon } from 'lucide-react';
interface Testimonial {
  name: string;
  location: string;
  rating: number;
  text: string;
}
const testimonials: Testimonial[] = [{
  name: 'Sarah M.',
  location: 'Austin, TX',
  rating: 5,
  text: "I was terrified of my 8-year-old's tantrums. Tonight was the first time in 2 years we had a screen-free dinner without a single tear."
}, {
  name: 'David R.',
  location: 'Orlando, FL',
  rating: 4,
  text: "The 90-second script felt weird at first, but it actually worked. He just... put it down. I'm shocked."
}, {
  name: 'Jessica K.',
  location: 'Phoenix, AZ',
  rating: 5,
  text: "Best $6 I've ever spent. I feel like I have my son back."
}, {
  name: 'Michelle T.',
  location: 'Denver, CO',
  rating: 4.5,
  text: 'My daughter actually asked to help with dinner instead of fighting me. This protocol is a game-changer.'
}, {
  name: 'Robert L.',
  location: 'Seattle, WA',
  rating: 5,
  text: 'Within 3 days, bedtime became peaceful again. No more sneaking the tablet under the covers.'
}, {
  name: 'Amanda P.',
  location: 'Chicago, IL',
  rating: 4.5,
  text: 'I was skeptical, but the brain-reset method really works. My kids are actually talking to each other again.'
}];
function StarRating({
  rating
}: {
  rating: number;
}) {
  const stars = [];
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 !== 0;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
  for (let i = 0; i < fullStars; i++) {
    stars.push(<StarIcon key={`full-${i}`} className="w-5 h-5 fill-yellow-400 text-yellow-400" data-id="element-195" />);
  }
  if (hasHalfStar) {
    stars.push(<div key="half" className="relative w-5 h-5" data-id="element-196">
        <StarIcon className="absolute inset-0 w-5 h-5 text-gray-300 fill-gray-300" data-id="element-197" />
        <div className="absolute inset-0 w-[50%] overflow-hidden" data-id="element-198">
          <StarIcon className="w-5 h-5 fill-yellow-400 text-yellow-400" data-id="element-199" />
        </div>
      </div>);
  }
  for (let i = 0; i < emptyStars; i++) {
    stars.push(<StarIcon key={`empty-${i}`} className="w-5 h-5 text-gray-300 fill-gray-300" data-id="element-200" />);
  }
  return <div className="flex gap-1" data-id="element-201">{stars}</div>;
}
function InteractiveStars({
  selected,
  onSelect
}: {
  selected: number;
  onSelect: (n: number) => void;
}) {
  const [hovered, setHovered] = useState(0);
  const display = hovered || selected;
  return <div className="flex gap-1" data-id="element-202">
      {[1, 2, 3, 4, 5].map(star => <button key={star} onMouseEnter={() => setHovered(star)} onMouseLeave={() => setHovered(0)} onClick={() => onSelect(star)} className="focus:outline-none" aria-label={`Rate ${star} stars`} data-id="element-203">
          <StarIcon className={`w-6 h-6 transition-colors ${star <= display ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300 fill-gray-300'}`} data-id="element-204" />
        </button>)}
    </div>;
}
export function TestimonialCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [comment, setComment] = useState('');
  const [selectedStars, setSelectedStars] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);
  const handleSubmit = () => {
    if (comment.trim() && selectedStars > 0) {
      setSubmitted(true);
    }
  };
  return <section className="w-full bg-gray-50 py-10 md:py-16 px-6" data-id="element-205">
      <div className="max-w-4xl mx-auto" data-id="element-206">
        <h2 className="font-black text-3xl md:text-4xl text-center mb-10 text-[#1a1a2e]" data-id="element-207">
          What Other Exhausted Parents Are Saying:
        </h2>

        {/* Carousel */}
        <div className="relative h-64 md:h-52 mb-10" data-id="element-208">
          <AnimatePresence mode="wait" data-id="element-209">
            <motion.div key={currentIndex} initial={{
            opacity: 0,
            x: 50
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -50
          }} transition={{
            duration: 0.5
          }} className="absolute inset-0" data-id="element-210">
              <div className="bg-white rounded-xl shadow-lg p-8 h-full flex flex-col justify-between" data-id="element-211">
                <div data-id="element-212">
                  <StarRating rating={testimonials[currentIndex].rating} data-id="element-213" />
                  <p className="text-lg md:text-xl text-[#1a1a2e] mb-4 mt-4 italic" data-id="element-214">
                    "{testimonials[currentIndex].text}"
                  </p>
                </div>
                <p className="font-semibold text-[#1a1a2e]" data-id="element-215">
                  — {testimonials[currentIndex].name},{' '}
                  {testimonials[currentIndex].location}
                </p>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-2 mb-10" data-id="element-216">
          {testimonials.map((_, index) => <button key={index} onClick={() => setCurrentIndex(index)} className={`w-3 h-3 rounded-full transition-colors ${index === currentIndex ? 'bg-red-600' : 'bg-gray-300'}`} aria-label={`Go to testimonial ${index + 1}`} data-id="element-217" />)}
        </div>

        {/* Featured Audio Testimonial */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mb-10 bg-[#1a1a2e] rounded-2xl p-6 md:p-8 shadow-xl relative overflow-hidden" data-id="element-218">
          <div className="absolute top-0 right-0 bg-red-600 text-white text-xs font-bold px-4 py-1.5 rounded-bl-xl" data-id="element-219">
            🎙️ AUDIO TESTIMONIAL
          </div>

          <div className="mt-4" data-id="element-220">
            <StarRating rating={5} data-id="element-221" />
            <p className="text-lg md:text-xl text-white mt-4 mb-4 italic font-medium leading-relaxed" data-id="element-222">
              "I recorded this because I couldn't believe it. My son put the
              iPad down on his own for the first time in a year. I literally
              cried. This protocol saved our family."
            </p>
            <p className="font-bold text-gray-300 mb-5" data-id="element-223">
              — Maria G., Nashville, TN
            </p>

            <p className="text-amber-400 font-bold text-sm md:text-base mb-4" data-id="element-224">
              🔊 Press play to hear her testimony
            </p>

            <div className="bg-white/10 p-4 rounded-xl" data-id="element-225">
              <audio controls className="w-full h-12" src="https://cdn.magicpatterns.com/uploads/hBWgkiXCL5MHGwr6CFLFNV/202603241132 (1).mp4" data-id="element-226">
                Your browser does not support the audio element.
              </audio>
            </div>
          </div>
        </motion.div>

        {/* Comment Input */}
        <div className="max-w-2xl mx-auto" data-id="element-227">
          <p className="text-sm font-semibold text-gray-600 mb-3" data-id="element-228">
            Leave your comment:
          </p>

          {submitted ? <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="bg-green-50 border-2 border-green-200 rounded-xl p-6 text-center" data-id="element-229">
              <CheckCircleIcon className="w-10 h-10 text-green-500 mx-auto mb-3" data-id="element-230" />
              <p className="font-bold text-lg text-[#1a1a2e]" data-id="element-231">
                Thank you for sharing!
              </p>
              <p className="text-gray-600 mt-1" data-id="element-232">
                Your review will appear shortly.
              </p>
            </motion.div> : <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden" data-id="element-233">
              <textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="Share your experience with the Screen-Free Kids Protocol..." className="w-full px-5 py-4 text-base text-[#1a1a2e] border-none outline-none resize-none h-24" rows={3} data-id="element-234" />
              <div className="flex items-center justify-between px-5 py-3 bg-gray-50 border-t border-gray-100" data-id="element-235">
                <InteractiveStars selected={selectedStars} onSelect={setSelectedStars} data-id="element-236" />
                <button onClick={handleSubmit} disabled={!comment.trim() || selectedStars === 0} className={`flex items-center gap-2 px-5 py-2 rounded-lg font-semibold text-sm transition-colors ${comment.trim() && selectedStars > 0 ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`} data-id="element-237">
                  <SendIcon className="w-4 h-4" data-id="element-238" />
                  Submit
                </button>
              </div>
            </div>}
        </div>
      </div>
    </section>;
}